import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaArrowLeft, FaEnvelope, FaUser, FaCalendarAlt, FaPlus } from 'react-icons/fa';
import { authService } from '../services/auth.service';
import '../components/Dashboard.css';

const DEPARTMENT_OPTIONS = [
  'Project Manager',
  'Rapid Prospect',
  'Copy Writing',
  'Designer',
  'Developer',
  'AI Developer',
  'Social Media',
  'CRM',
  'SEO/GEO',
];

const Users: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = authService.getUser();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('All Roles');
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [roleDraftByUserId, setRoleDraftByUserId] = useState<Record<string, string>>({});
  const [passwordDraftByUserId, setPasswordDraftByUserId] = useState<Record<string, string>>({});
  const canManageUsers = currentUser?.isHeadPM || currentUser?.role === 'FOUNDER/CEO';
  const emptyNewUser = { name: '', email: '', role: 'Copy Writing', password: '' };
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState(emptyNewUser);
  const [addingUser, setAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [addedUser, setAddedUser] = useState<{ name: string; email: string; password: string } | null>(null);

  const closeAddUser = () => {
    setShowAddUser(false);
    setNewUser(emptyNewUser);
    setAddUserError('');
    setAddedUser(null);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddUserError('');
    if (newUser.password.length < 8) {
      setAddUserError('The temporary password must be at least 8 characters.');
      return;
    }
    try {
      setAddingUser(true);
      await authService.createUser(newUser);
      setAddedUser({ name: newUser.name, email: newUser.email, password: newUser.password });
      setNewUser(emptyNewUser);
      await loadUsers();
    } catch (error: any) {
      const message = error?.response?.data?.message;
      setAddUserError(Array.isArray(message) ? message.join(', ') : message || 'Could not add this person. Please try again.');
    } finally {
      setAddingUser(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await authService.getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only allow Project Managers and Founder/CEO to access this page
    const currentUser = authService.getUser();
    const allowed = currentUser?.role === 'Project Manager' || currentUser?.role === 'FOUNDER/CEO';
    if (!currentUser || !allowed) {
      navigate('/dashboard', { replace: true });
      return;
    }
    setIsAuthorized(true);
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRoleDraftByUserId((prev) => {
      const next = { ...prev };
      users.forEach((u) => {
        if (!next[u.id]) next[u.id] = u.role;
      });
      return next;
    });
  }, [users]);

  // Don't render until we've checked authorization
  if (isAuthorized === null) {
    return null;
  }

  // If not authorized, return null (redirect will happen)
  if (!isAuthorized) {
    return null;
  }

  const getFilteredUsers = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term) ||
          u.role?.toLowerCase().includes(term)
      );
    }

    // Apply role filter
    if (roleFilter !== 'All Roles') {
      filtered = filtered.filter((u) => u.role === roleFilter);
    }

    return filtered;
  };

  const filteredUsers = getFilteredUsers();
  const uniqueRoles = Array.from(new Set(users.map((u) => u.role))).sort();

  if (loading) {
    return (
      <div className="dashboard premium">
        <div className="loading-skeleton">
          <div className="skeleton-header"></div>
          <div className="skeleton-cards">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-card"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard premium">
      <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'none',
              border: 'none',
              color: '#111111',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              marginBottom: '1rem',
              padding: '0.5rem 0',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <FaArrowLeft /> Back to Dashboard
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #111111 0%, #767676 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.5rem',
              }}
            >
              <FaUsers />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.875rem', fontWeight: 700, color: '#1c1c1c' }}>
                Registered Users
              </h1>
              <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
                Manage and view all registered users in the system
              </p>
            </div>
          </div>
          {canManageUsers && (
            <button
              type="button"
              onClick={() => setShowAddUser(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', minHeight: 44, padding: '0.625rem 1rem', border: 0, borderRadius: 9, background: '#111111', color: '#fff', fontWeight: 650, cursor: 'pointer' }}
            >
              <FaPlus aria-hidden="true" /> Add teammate
            </button>
          )}
        </div>

        {showAddUser && (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) closeAddUser(); }}
            style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.35)' }}
          >
            <div role="dialog" aria-modal="true" aria-label="Add teammate" style={{ width: '100%', maxWidth: 440, padding: 24, borderRadius: 14, background: '#fff', boxShadow: '0 24px 70px rgba(0,0,0,0.25)' }}>
              <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 720, color: '#111111' }}>{addedUser ? 'Teammate added' : 'Add teammate'}</h2>
              {addedUser ? (
                <>
                  <p style={{ margin: '0 0 14px', color: '#555', lineHeight: 1.5 }}>
                    Send {addedUser.name} these details privately. This password won't be shown again, and they can change it with "Forgot password".
                  </p>
                  <div style={{ padding: 12, marginBottom: 16, borderRadius: 9, background: '#f4f4f4', color: '#111111', overflowWrap: 'anywhere', lineHeight: 1.6 }}>
                    Email: {addedUser.email}<br />Temporary password: {addedUser.password}
                  </div>
                  <button type="button" onClick={closeAddUser} style={{ minHeight: 44, width: '100%', border: 0, borderRadius: 9, background: '#111111', color: '#fff', fontWeight: 650, cursor: 'pointer' }}>Done</button>
                </>
              ) : (
                <form onSubmit={handleAddUser}>
                  <p style={{ margin: '0 0 14px', color: '#555', lineHeight: 1.5 }}>They can log in straight away with the temporary password you set.</p>
                  {[
                    { key: 'name', label: 'Full name', type: 'text', auto: 'off' },
                    { key: 'email', label: 'Work email', type: 'email', auto: 'off' },
                    { key: 'password', label: 'Temporary password', type: 'text', auto: 'new-password' },
                  ].map((field) => (
                    <label key={field.key} style={{ display: 'block', marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#333' }}>
                      {field.label}
                      <input
                        required
                        type={field.type}
                        autoComplete={field.auto}
                        name={`new-user-${field.key}`}
                        value={(newUser as any)[field.key]}
                        onChange={(e) => setNewUser((current) => ({ ...current, [field.key]: e.target.value }))}
                        style={{ display: 'block', width: '100%', marginTop: 5, padding: '0.625rem 0.75rem', border: '1px solid #d4d4d4', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
                      />
                    </label>
                  ))}
                  <label style={{ display: 'block', marginBottom: 14, fontSize: 13, fontWeight: 600, color: '#333' }}>
                    Department
                    <select
                      name="new-user-role"
                      value={newUser.role}
                      onChange={(e) => setNewUser((current) => ({ ...current, role: e.target.value }))}
                      style={{ display: 'block', width: '100%', marginTop: 5, padding: '0.625rem 0.75rem', border: '1px solid #d4d4d4', borderRadius: 8, fontSize: 16, background: '#fff' }}
                    >
                      {DEPARTMENT_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
                      {currentUser?.role === 'FOUNDER/CEO' && <option value="FOUNDER/CEO">FOUNDER/CEO</option>}
                    </select>
                  </label>
                  {addUserError && <p role="alert" style={{ margin: '0 0 12px', color: '#b84436', fontSize: 13 }}>{addUserError}</p>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={closeAddUser} style={{ flex: 1, minHeight: 44, border: '1px solid #d4d4d4', borderRadius: 9, background: '#fff', color: '#111111', fontWeight: 650, cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" disabled={addingUser} style={{ flex: 1, minHeight: 44, border: 0, borderRadius: 9, background: '#111111', color: '#fff', fontWeight: 650, cursor: 'pointer', opacity: addingUser ? 0.6 : 1 }}>{addingUser ? 'Adding…' : 'Add teammate'}</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem',
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: '250px' }}>
            <input
              type="text"
              name="users-search"
              autoComplete="off"
              placeholder="Search by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#111111')}
              onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
            />
          </div>
          <div style={{ minWidth: '200px' }}>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                fontSize: '0.875rem',
                outline: 'none',
                background: 'white',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#111111')}
              onBlur={(e) => (e.target.style.borderColor = '#e8e8e8')}
            >
              <option>All Roles</option>
              {uniqueRoles.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
          </div>
        </div>

        {/* Table */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr style={{ background: '#ffffff', borderBottom: '2px solid #e8e8e8' }}>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    User
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Team Lead
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Head PM
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Department
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Access
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Password Reset
                  </th>
                  <th
                    style={{
                      padding: '1rem 1.5rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#444444',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      style={{
                        padding: '3rem',
                        textAlign: 'center',
                        color: '#a6a6a6',
                        fontSize: '0.875rem',
                      }}
                    >
                      No users found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((userItem, index) => {
                    const isActive = userItem.isActive !== false;
                    return (
                      <tr
                        key={userItem.id}
                        style={{
                          borderBottom: index < filteredUsers.length - 1 ? '1px solid #f4f4f4' : 'none',
                          transition: 'background 0.2s',
                          opacity: isActive ? 1 : 0.65,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                      >
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #111111 0%, #767676 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: '1rem',
                                flexShrink: 0,
                              }}
                            >
                              {userItem.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, color: '#1c1c1c', fontSize: '0.9375rem' }}>
                                {userItem.name}
                              </div>
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#a6a6a6',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  marginTop: '0.125rem',
                                }}
                              >
                                <FaUser style={{ fontSize: '0.625rem' }} />
                                ID: {userItem.id.substring(0, 8)}...
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#444444',
                              fontSize: '0.875rem',
                            }}
                          >
                            <FaEnvelope style={{ color: '#9ca3af', fontSize: '0.875rem' }} />
                            {userItem.email}
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <button
                            disabled={updatingUserId === userItem.id || !canManageUsers}
                            onClick={async () => {
                              try {
                                setUpdatingUserId(userItem.id);
                                const newValue = !userItem.isTeamLead;
                                const updated = await authService.setTeamLead(
                                  userItem.id,
                                  newValue
                                );
                                setUsers((prev) =>
                                  prev.map((u) =>
                                    u.id === updated.id ? { ...u, ...updated } : u
                                  )
                                );
                              } catch (error) {
                                console.error('Failed to update team lead status:', error);
                                alert('Failed to update team lead status. Please try again.');
                              } finally {
                                setUpdatingUserId(null);
                              }
                            }}
                            style={{
                              padding: '0.375rem 0.75rem',
                              borderRadius: '999px',
                              border: '1px solid',
                              borderColor: userItem.isTeamLead ? '#10b981' : '#e8e8e8',
                              background: userItem.isTeamLead ? '#ecfdf3' : 'white',
                              color: userItem.isTeamLead ? '#047857' : '#6b7280',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              cursor: updatingUserId === userItem.id || !canManageUsers ? 'not-allowed' : 'pointer',
                              minWidth: '90px',
                            }}
                          >
                            {userItem.isTeamLead ? 'Team Lead' : 'Make Lead'}
                          </button>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          {userItem.role === 'Project Manager' ? (
                            <button
                              disabled={updatingUserId === userItem.id || !canManageUsers}
                              onClick={async () => {
                                try {
                                  setUpdatingUserId(userItem.id);
                                  const newValue = !userItem.isHeadPM;
                                  const updated = await authService.setHeadPM(
                                    userItem.id,
                                    newValue
                                  );
                                  setUsers((prev) =>
                                    prev.map((u) =>
                                      u.id === updated.id ? { ...u, ...updated } : u
                                    )
                                  );
                                } catch (error: any) {
                                  console.error('Failed to update Head PM status:', error);
                                  alert(
                                    error?.response?.data?.message ||
                                      'Failed to update Head PM status. Only Project Managers can be Head PM.'
                                  );
                                } finally {
                                  setUpdatingUserId(null);
                                }
                              }}
                              style={{
                                padding: '0.375rem 0.75rem',
                                borderRadius: '999px',
                                border: '1px solid',
                                borderColor: userItem.isHeadPM ? '#111111' : '#e8e8e8',
                                background: userItem.isHeadPM ? '#f6f6f6' : 'white',
                                color: userItem.isHeadPM ? '#333333' : '#6b7280',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                cursor: updatingUserId === userItem.id || !canManageUsers ? 'not-allowed' : 'pointer',
                                minWidth: '90px',
                              }}
                            >
                              {userItem.isHeadPM ? 'Head PM' : 'Make Head PM'}
                            </button>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <select
                              value={roleDraftByUserId[userItem.id] || userItem.role}
                              onChange={(e) =>
                                setRoleDraftByUserId((prev) => ({ ...prev, [userItem.id]: e.target.value }))
                              }
                              disabled={!canManageUsers || updatingUserId === userItem.id}
                              style={{
                                padding: '0.38rem 0.45rem',
                                border: '1px solid #e8e8e8',
                                borderRadius: 8,
                                fontSize: '0.76rem',
                                background: 'white',
                              }}
                            >
                              {DEPARTMENT_OPTIONS.map((roleOption) => (
                                <option key={roleOption} value={roleOption}>
                                  {roleOption}
                                </option>
                              ))}
                            </select>
                            <button
                              disabled={
                                !canManageUsers ||
                                updatingUserId === userItem.id ||
                                (roleDraftByUserId[userItem.id] || userItem.role) === userItem.role
                              }
                              onClick={async () => {
                                try {
                                  setUpdatingUserId(userItem.id);
                                  const updated = await authService.updateUserRole(
                                    userItem.id,
                                    roleDraftByUserId[userItem.id] || userItem.role
                                  );
                                  setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
                                } catch (error: any) {
                                  console.error('Failed to update user role:', error);
                                  alert(error?.response?.data?.message || 'Failed to update department.');
                                } finally {
                                  setUpdatingUserId(null);
                                }
                              }}
                              style={{
                                padding: '0.35rem 0.5rem',
                                border: '1px solid #111111',
                                borderRadius: 8,
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                color: '#111111',
                                background: '#efefef',
                                cursor: 'pointer',
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <button
                            disabled={!canManageUsers || updatingUserId === userItem.id}
                            onClick={async () => {
                              try {
                                setUpdatingUserId(userItem.id);
                                const updated = await authService.setUserAccess(userItem.id, !isActive);
                                setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
                              } catch (error: any) {
                                console.error('Failed to update user access:', error);
                                alert(error?.response?.data?.message || 'Failed to update user access.');
                              } finally {
                                setUpdatingUserId(null);
                              }
                            }}
                            style={{
                              padding: '0.38rem 0.6rem',
                              borderRadius: '999px',
                              border: '1px solid',
                              borderColor: isActive ? '#ef4444' : '#10b981',
                              background: isActive ? '#fef2f2' : '#ecfdf3',
                              color: isActive ? '#b91c1c' : '#047857',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              cursor: !canManageUsers || updatingUserId === userItem.id ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isActive ? 'Revoke Access' : 'Restore Access'}
                          </button>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <input
                              type="password"
                              placeholder="New password"
                              value={passwordDraftByUserId[userItem.id] || ''}
                              onChange={(e) =>
                                setPasswordDraftByUserId((prev) => ({ ...prev, [userItem.id]: e.target.value }))
                              }
                              disabled={!canManageUsers || updatingUserId === userItem.id}
                              style={{
                                width: 130,
                                padding: '0.38rem 0.45rem',
                                border: '1px solid #e8e8e8',
                                borderRadius: 8,
                                fontSize: '0.74rem',
                              }}
                            />
                            <button
                              disabled={
                                !canManageUsers ||
                                updatingUserId === userItem.id ||
                                !passwordDraftByUserId[userItem.id] ||
                                passwordDraftByUserId[userItem.id].length < 6
                              }
                              onClick={async () => {
                                try {
                                  setUpdatingUserId(userItem.id);
                                  await authService.adminResetUserPassword(
                                    userItem.id,
                                    passwordDraftByUserId[userItem.id]
                                  );
                                  setPasswordDraftByUserId((prev) => ({ ...prev, [userItem.id]: '' }));
                                  alert('Password reset successfully.');
                                } catch (error: any) {
                                  console.error('Failed to reset password:', error);
                                  alert(error?.response?.data?.message || 'Failed to reset password.');
                                } finally {
                                  setUpdatingUserId(null);
                                }
                              }}
                              style={{
                                padding: '0.35rem 0.5rem',
                                border: '1px solid #111111',
                                borderRadius: 8,
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                color: '#111111',
                                background: '#f3f3f3',
                                cursor: 'pointer',
                              }}
                            >
                              Reset
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              color: '#6b7280',
                              fontSize: '0.875rem',
                            }}
                          >
                            <FaCalendarAlt style={{ color: '#9ca3af', fontSize: '0.875rem' }} />
                            {new Date(userItem.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;

